import React, { useState, useEffect } from 'react';
import {
  Modal,
  Header,
  List,
  Rating,
  Icon,
  Loader,
  Divider,
  Message
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { authUtil } from '../../utils';
import Avatar from '../avatar/avatar';

const UserReviewsModal = ({ open, onClose, user, t }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && user) {
      fetchUserReviews();
    }
  }, [open, user]);

  const fetchUserReviews = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const idToken = await authUtil.getFreshIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/users/${user._id}/reviews`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken
          }
        }
      );

      if (response.ok) {
        const reviewsData = await response.json();
        setReviews(reviewsData || []);
      } else {
        throw new Error('Failed to fetch reviews');
      }
    } catch (error) {
      console.error('Failed to fetch user reviews:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setReviews([]);
    setError(null);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('UnknownDate');
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="small"
      closeIcon
    >
      <Modal.Header>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            name={user?.displayName}
            src={user?.photoUrl}
            diameter={30}
            style={{ marginRight: 10 }}
          />
{t('ReviewsFor', { name: user?.displayName || 'User' })}
        </div>
      </Modal.Header>
      
      <Modal.Content>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Loader active inline />
            <p>{t('LoadingReviews')}</p>
          </div>
        ) : error ? (
          <Message negative>
            <Message.Header>{t('ErrorLoadingReviews')}</Message.Header>
            <p>{error}</p>
          </Message>
        ) : reviews.length === 0 ? (
          <Message info>
            <Message.Header>{t('NoReviewsYet')}</Message.Header>
            <p>{t('NoReviewsYetDescription')}</p>
          </Message>
        ) : (
          <div>
            <Header as="h4" style={{ marginBottom: '20px' }}>
              {user?.averageRating ? (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                  <Rating
                    icon="star"
                    rating={Math.round(user.averageRating * 2) / 2}
                    maxRating={5}
                    disabled
                    size="small"
                  />
                  <span style={{ fontWeight: 'normal', fontSize: '0.9em', marginLeft: '10px' }}>
                    ({user.averageRating}/5)
                  </span>
                </div>
              ) : (
                <Icon name="star outline" />
              )}
{t('ReviewCount', { count: reviews.length })}
            </Header>

            <List divided relaxed>
              {reviews.map((review, index) => (
                <List.Item key={review._id || index} style={{ padding: '15px 0' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      {review.reviewer?.displayName && (
                        <Avatar
                          name={review.reviewer.displayName}
                          src={review.reviewer.photoUrl}
                          diameter={25}
                          style={{ marginRight: 10 }}
                        />
                      )}
                      <div>
                        <strong>
                          {review.reviewer?.displayName || t('Anonymous')}
                        </strong>
                        <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px' }}>
                          {formatDate(review.createdAt)}
                          {review.book?.title && (
                            <span> • {t('ForBook', { title: review.book.title })}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ marginLeft: '35px' }}>
                      <Rating
                        icon="star"
                        rating={review.rating}
                        maxRating={5}
                        disabled
                        size="small"
                      />
                      
                      {review.text && (
                        <div style={{ 
                          marginTop: '8px',
                          padding: '10px',
                          backgroundColor: 'var(--br-raised-surface)',
                          borderRadius: '4px',
                          fontStyle: 'italic',
                          fontSize: '0.9em',
                          lineHeight: '1.4'
                        }}>
                          "{review.text}"
                        </div>
                      )}
                    </div>
                  </div>
                </List.Item>
              ))}
            </List>
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default withTranslation()(UserReviewsModal);
